import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FlytWings } from './FlytWings';
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle2,
  ShieldCheck,
  Briefcase,
  Sparkles,
  GraduationCap,
  BadgeCheck,
  MessageSquare,
  DollarSign,
  Users,
  Bell,
  Trophy,
  Bookmark,
  Share2,
  BarChart3,
  Play,
  Send,
  Video,
  LayoutGrid,
  Building2,
  Image as ImageIcon,
  UserPlus,
  Upload,
  AlertCircle,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import SocialLinksDots from './SocialLinksDots';
import { useUserAuth } from '../contexts/UserAuthContext';
import { AppStoreBadge } from './AppStorePromo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * PublicHomeLandingBlock (iter223 · comprehensive scroll experience)
 * ────────────────────────────────────────────────────────────────
 * Landing shown only to LOGGED-OUT visitors on `/`. Inserted inside
 * `NetworkPage` immediately after the Ozzie hero.
 *
 * Rebuilt so the visitor scrolls through a MINI-TOUR of the real
 * product:
 *   Hero pitch  →  Athlete Profile Card (Josiah replica)
 *              →  Career Timeline (Ozzie milestones)
 *              →  Bio-Link (FLYT Athletes replica)
 *              →  Tabbed Persona Explorer (Athlete/Agent/Brand/Program)
 *
 * Every visual is a HAND-COMPOSED static mockup — no iframes, so
 * mobile scroll never rubber-bands and tap targets stay aligned.
 * Ambient notification pills float over the cards to add life.
 */

/* ── Ambient notification pill ─────────────────────────────────
 * Small Apple-UI-flavoured notification chip that floats over the
 * upper corner of a visual. Used to signal "the platform is live". */
function NotifBubble({ label, sub, tone = 'blue', icon: Icon = Bell, positionClass }) {
  const toneMap = {
    blue: 'bg-blue-500/85 text-white',
    emerald: 'bg-emerald-500/85 text-white',
    fuchsia: 'bg-fuchsia-500/85 text-white',
  };
  return (
    <div
      className={`absolute ${positionClass} z-20 max-w-[220px] rounded-2xl backdrop-blur-xl shadow-2xl ring-1 ring-white/15 ${toneMap[tone] || toneMap.blue} px-3 py-2 flex items-center gap-2.5 animate-fade-in`}
    >
      <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" strokeWidth={2.2} />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-bold leading-tight truncate">{label}</p>
        <p className="text-[9px] uppercase tracking-widest opacity-80 leading-tight truncate">{sub}</p>
      </div>
    </div>
  );
}

/* ── VISUAL 01 · Athlete Network Profile Card ──────────────────
 * Faithful replica of the Josiah profile screenshot Jay shared:
 * circular avatar + verified dot, name + NFL pill, position/team
 * pills, MaxPreps stats badge, PARTNERS row with logo chips,
 * Follow / Get Updates / Share row, blue "Business inquiries"
 * button, and the STORY/CAREER/ARTICLES/SHORTS tabs at the
 * bottom. This is exactly what an athlete gets when they log in. */
function ProfileCardVisual({ athlete, stories = [] }) {
  // Chris pinned by product direction — landing profile visual shows
  // Chris @ FLYT Studios so visitors read a clear "Flythouse is built
  // by athletes, for athletes" signal. Tabs are LIVE — clicking Story
  // / Career / Articles / Shorts swaps the content pane below the
  // header so the mockup itself is a mini interactive demo.
  const name = 'CHRIS';
  const team = 'FLYT Studios';
  const sport = 'Football';
  const [tab, setTab] = useState('story');

  // Career milestones for the CAREER tab — mirrors what a real
  // athlete's Career tab renders on flythouse.com/{slug}.
  const careerMilestones = [
    { year: '2020', title: 'Committed · University of Texas', tag: 'RECRUIT' },
    { year: '2021', title: 'Freshman All-American', tag: null },
    { year: '2023', title: 'Signed with FLYT Athletes', tag: 'AGENCY' },
    { year: '2024', title: 'Big 12 Championship · MVP', tag: 'AWARD' },
    { year: '2025', title: 'NIL Deal · Gatorade + Under Armour', tag: 'BRAND' },
  ];

  // Story-arc chapters — each renders as a full-bleed story card
  // (mirrors the network profile Story tile: uppercase name, italic
  // serif subtitle, description, blue "Get Access" pill). The arc
  // slides horizontally on mobile with scroll-snap so visitors can
  // swipe through Chris's chapters without leaving the profile card.
  const storyArc = [
    {
      year: '2020',
      chapter: 'THE FIRST SNAP',
      subtitle: 'Where the story begins.',
      body: 'A high-school walk-on who nobody scouted. The tape that changed the room.',
      media: 'video',
    },
    {
      year: '2022',
      chapter: 'FROM WALK-ON',
      subtitle: 'Chasing Greatness Beyond the Spotlight',
      body: 'Step inside their world. Long-form video, brand drops, and the moments no one else captures.',
      media: 'article',
    },
    {
      year: '2024',
      chapter: 'MVP MOMENT',
      subtitle: 'National TV. Big 12 Championship.',
      body: 'The catch that anchored the drive that won the trophy — with the full behind-the-scenes.',
      media: 'shorts',
    },
    {
      year: '2025',
      chapter: 'BEYOND THE FIELD',
      subtitle: 'Business. Brand. Legacy.',
      body: 'How Chris turned one moment into a five-figure NIL portfolio and a personal brand.',
      media: 'article',
    },
  ];

  // Shorts — visual grid representing the SHORTS tab. Uses the
  // fetched athlete photo as thumbnail for authenticity.
  const shorts = ['0:15 · 240K', '0:22 · 512K', '0:18 · 189K', '0:31 · 96K'];

  return (
    <div className="relative w-full">
      {/* Floating notifications around the card */}
      <NotifBubble
        label="New follower · +1"
        sub="From your network"
        tone="blue"
        icon={Users}
        positionClass="-top-3 -right-2 sm:-right-6"
      />
      <NotifBubble
        label="FLYT Athletes"
        sub="Wants to connect"
        tone="emerald"
        icon={MessageSquare}
        positionClass="-bottom-2 -left-2 sm:-left-6"
      />

      <div className="rounded-3xl bg-neutral-950 ring-1 ring-white/10 p-5 sm:p-6">
        {/* Header — avatar + name + NFL pill */}
        <div className="flex items-start gap-4">
          <div className="relative shrink-0">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden ring-2 ring-white/10 bg-gradient-to-br from-blue-800 to-black">
              {athlete?.photo && (
                <img src={athlete.photo} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-blue-500 ring-2 ring-black flex items-center justify-center">
              <CheckCircle2 className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
            </div>
          </div>
          <div className="min-w-0 flex-1 pt-1">
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">
              DREAMS LEAGUE · NETWORK · {sport.toUpperCase()}
            </p>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <h4 className="font-display uppercase text-white text-3xl sm:text-4xl leading-none tracking-tight">
                {name}
              </h4>
              <span className="inline-flex items-center px-2 py-0.5 rounded-md ring-1 ring-yellow-400/60 text-[10px] uppercase tracking-widest font-bold text-yellow-300">
                NFL
              </span>
            </div>
            <p className="mt-1.5 text-white/60 text-sm">{team}</p>
          </div>
        </div>

        {/* Action row */}
        <div className="mt-5 flex items-center gap-2 flex-wrap">
          {[
            { icon: Bookmark, label: 'Follow' },
            { icon: Bell, label: 'Get Updates' },
            { icon: Share2, label: 'Share' },
          ].map((a) => {
            const Icon = a.icon;
            return (
              <div key={a.label} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full ring-1 ring-white/10 bg-white/[0.03] text-white text-[11px] font-bold">
                <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                {a.label}
              </div>
            );
          })}
        </div>

        {/* Position/team pills */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {['TE', '#55', 'DALLAS'].map((p) => (
            <span key={p} className="inline-flex items-center px-2.5 py-1 rounded-full ring-1 ring-white/15 bg-white/[0.02] text-white text-[10px] font-bold tracking-widest">
              {p}
            </span>
          ))}
        </div>

        {/* MaxPreps badge */}
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-full ring-1 ring-red-500/40 bg-red-500/10">
          <div className="w-6 h-6 rounded-md bg-red-500/40 flex items-center justify-center">
            <BarChart3 className="w-3.5 h-3.5 text-red-300" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <p className="text-white text-[11px] font-bold uppercase tracking-widest">MaxPreps</p>
            <p className="text-white/50 text-[9px] uppercase tracking-widest">Verified stats</p>
          </div>
        </div>

        {/* Partners + business inquiries row */}
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-white/40">PARTNERS</p>
          <div className="flex -space-x-1.5">
            {['bg-neutral-800', 'bg-neutral-700'].map((c, i) => (
              <div key={i} className={`w-7 h-7 rounded-full ${c} ring-2 ring-black flex items-center justify-center text-white text-[9px] font-black`}>
                {i === 0 ? 'FLYT' : 'FL'}
              </div>
            ))}
          </div>
          <div className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-blue-500 text-white text-[11px] font-bold shrink-0">
            <Briefcase className="w-3.5 h-3.5" strokeWidth={2} />
            Business inquiries
          </div>
        </div>

        {/* Tabs strip — CLICKABLE so visitors can preview each pane
            without leaving the landing. Same interaction the real
            profile has. */}
        <div className="mt-5 pt-4 border-t border-white/5 flex items-center gap-4 overflow-x-auto scrollbar-hide">
          {[
            { id: 'story',    label: 'STORY',    count: null },
            { id: 'career',   label: 'CAREER',   count: 5 },
            { id: 'articles', label: 'ARTICLES', count: stories.length || 4 },
            { id: 'shorts',   label: 'SHORTS',   count: 4 },
          ].map((t) => {
            const active = tab === t.id;
            return (
              <button
                type="button"
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`landing-profile-tab-${t.id}`}
                className={`flex items-center gap-1.5 pb-2 text-[10px] uppercase tracking-widest font-bold transition ${active ? 'text-white border-b-2 border-blue-400' : 'text-white/40 hover:text-white/70'}`}
              >
                <Sparkles className={`w-3 h-3 ${active ? 'text-blue-300' : ''}`} />
                {t.label}
                {t.count !== null && (
                  <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] ${active ? 'bg-blue-500 text-white' : 'bg-white/10 text-white/50'}`}>{t.count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab content panel */}
        <div className="mt-4">
          {tab === 'story' && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span aria-hidden className="h-px w-5 bg-blue-300/40" />
                <p className="font-display text-[9px] uppercase tracking-[0.28em] text-blue-300/85">The Arc · swipe →</p>
              </div>
              {/* iter229 · Mirrors the real `CareerTimeline` arc used on
                  /network/{slug} — slim horizontal rail with year labels,
                  dots per chapter, and a soft blue progress sweep. No
                  more chunky year boxes. */}
              <div className="-mx-5 sm:-mx-6 relative">
                <div
                  className="overflow-x-auto no-scrollbar pb-1"
                  style={{ WebkitOverflowScrolling: 'touch' }}
                  data-testid="landing-story-arc-scroller"
                >
                  <ol
                    className="relative inline-flex items-start gap-6 sm:gap-8"
                    style={{ paddingTop: 4, paddingLeft: 24, paddingRight: 32 }}
                  >
                    {/* base hair-thin rail — top matches the vertical
                        center of the dot row below the year text */}
                    <span aria-hidden className="absolute left-0 right-0 top-[39px] h-px bg-white/10" />
                    {/* progress sweep — soft blue glow across the arc */}
                    <span
                      aria-hidden
                      className="absolute left-0 top-[39px] h-px"
                      style={{
                        width: '100%',
                        background: 'linear-gradient(90deg, rgba(107,184,240,0) 0%, rgba(107,184,240,0.9) 22%, rgba(180,222,255,1) 50%, rgba(107,184,240,0.9) 78%, rgba(107,184,240,0) 100%)',
                        boxShadow: '0 0 12px rgba(107,184,240,0.45)',
                      }}
                    />
                    {storyArc.map((s, i) => {
                      const current = i === storyArc.length - 1;
                      return (
                        <li
                          key={s.year}
                          className="relative shrink-0 w-[88px] sm:w-[104px] text-center"
                          data-testid={`landing-story-chapter-${i}`}
                        >
                          <p className={`font-display text-[13px] sm:text-[14px] tracking-wider mb-2 leading-none ${current ? 'text-white' : 'text-white/80'}`}>
                            {s.year}
                          </p>
                          <span
                            className="relative inline-flex items-center justify-center"
                            style={{ height: 22 }}
                          >
                            <span
                              className={`relative z-[1] rounded-full ${current ? 'w-2.5 h-2.5 bg-white ring-[1.5px] ring-blue-300/80' : 'w-1.5 h-1.5 bg-white/90'}`}
                            />
                            {current && (
                              <span aria-hidden className="absolute inline-flex w-6 h-6 rounded-full bg-blue-400/15 animate-ping" />
                            )}
                          </span>
                          <p className={`font-italic mt-2 text-[10px] leading-snug px-0.5 ${current ? 'text-blue-200' : 'text-gray-400/90'}`}>
                            {s.chapter}
                          </p>
                        </li>
                      );
                    })}
                  </ol>
                </div>
                {/* edge fades so the arc feathers into the black card */}
                <span
                  aria-hidden
                  className="pointer-events-none absolute left-0 top-0 bottom-1 w-8"
                  style={{ background: 'linear-gradient(to right, rgba(10,10,10,1) 0%, rgba(10,10,10,0) 100%)' }}
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute right-0 top-0 bottom-1 w-8"
                  style={{ background: 'linear-gradient(to left, rgba(10,10,10,1) 0%, rgba(10,10,10,0) 100%)' }}
                />
              </div>
              {/* Latest chapter caption — mimics the real Story tile's
                  serif subtitle + body under the arc. Keeps a small
                  taste of the story WITHOUT the giant box grid. */}
              <div className="mt-4 rounded-xl ring-1 ring-white/8 bg-white/[0.02] px-3 py-2.5">
                <p className="text-[9px] uppercase tracking-[0.25em] font-bold text-blue-300 flex items-center gap-1.5">
                  <span className="w-1 h-1 rounded-full bg-blue-400" />
                  Latest chapter · {storyArc[storyArc.length - 1].year}
                </p>
                <div className="mt-1 flex items-center gap-1.5">
                  <p className="font-display uppercase text-white text-sm leading-none tracking-tight">
                    {storyArc[storyArc.length - 1].chapter}
                  </p>
                  <CheckCircle2 className="w-3 h-3 text-blue-400 shrink-0" strokeWidth={2.5} />
                </div>
                <p className="mt-1 text-white/75 text-[11px] leading-snug italic" style={{ fontFamily: 'Georgia, serif' }}>
                  {storyArc[storyArc.length - 1].subtitle}
                </p>
              </div>
            </div>
          )}

          {tab === 'career' && (
            <div className="relative pl-6">
              <div aria-hidden className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-emerald-400/40 via-emerald-400/20 to-transparent" />
              <div className="space-y-2">
                {careerMilestones.map((m) => (
                  <div key={m.year} className="relative">
                    <div className="absolute -left-6 top-3 w-3 h-3 rounded-full bg-emerald-400 ring-4 ring-black shadow-[0_0_8px_rgba(52,211,153,0.5)]" />
                    <div className="rounded-xl ring-1 ring-white/10 bg-white/[0.02] px-3 py-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 ring-emerald-400/40 bg-emerald-400/10 text-emerald-300 text-[9px] uppercase tracking-widest font-bold">
                          <Sparkles className="w-2.5 h-2.5" />
                          Milestone
                        </span>
                        <span className="text-white/50 text-[10px] uppercase tracking-widest">{m.year}</span>
                        {m.tag && (
                          <span className="text-white/40 text-[9px] uppercase tracking-widest">· {m.tag}</span>
                        )}
                      </div>
                      <p className="mt-1 text-white text-[13px] font-bold">{m.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'articles' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(stories.length ? stories.slice(0, 4) : [
                { name: 'The Making of a Big 12 MVP', slug: 'story-1', sport: 'Football' },
                { name: 'How Chris Built a Bio That Works', slug: 'story-2', sport: 'NIL' },
                { name: '5 Deals in 5 Weeks — a Playbook', slug: 'story-3', sport: 'Brands' },
                { name: 'Inside the Locker Room · Week 6', slug: 'story-4', sport: 'Football' },
              ]).map((s) => (
                <div key={s.slug} className="rounded-xl overflow-hidden ring-1 ring-white/10 bg-white/[0.02]">
                  <div className="relative aspect-video bg-neutral-900">
                    {(s.thumbnail || s.cover_image) ? (
                      <img src={s.thumbnail || s.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-800 via-black to-fuchsia-800" />
                    )}
                    <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute bottom-1.5 left-2 right-2">
                      <p className="text-[9px] uppercase tracking-widest font-bold text-blue-300">{s.sport || 'Feature'}</p>
                      <p className="mt-0.5 text-white text-[11px] font-bold leading-tight line-clamp-2">
                        {s.name}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'shorts' && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {shorts.map((meta, i) => (
                <div key={i} className="rounded-xl overflow-hidden ring-1 ring-white/10 bg-neutral-900 relative aspect-[9/16]">
                  {athlete?.photo && (
                    <img src={athlete.photo} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: `brightness(${0.5 + (i % 2) * 0.15})` }} />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
                    <Play className="w-3 h-3 fill-white text-white" />
                  </div>
                  <div className="absolute bottom-1.5 left-1.5 right-1.5">
                    <p className="text-white text-[9px] font-bold">{meta}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── VISUAL 02 · Career Timeline ──────────────────────────────
 * Vertical timeline with green dots + connecting line + milestone
 * cards. Modeled after Jay's "THE JOURNEY · CAREER TIMELINE"
 * screenshot for Ozzie. Highest-milestone card expands into a
 * stat grid to show the full richness. */
function CareerTimelineVisual() {
  const milestones = [
    { year: '2013', title: 'Atlanta Braves', tag: 'FREE AGENT' },
    { year: '2017', title: 'Major League Debut', tag: null },
    { year: '2018', title: 'Opening Day Start', tag: null },
    {
      year: '2019',
      title: 'Led the League · 189 Base Hits',
      tag: null,
      stats: [
        { k: 'Games', v: '160' },
        { k: 'At-Bats', v: '640' },
        { k: 'Hits', v: '189' },
        { k: 'AVG', v: '.295' },
        { k: 'HR', v: '24' },
        { k: 'RBI', v: '86' },
      ],
    },
  ];
  return (
    <div className="relative w-full">
      {/* Floating notif */}
      <NotifBubble
        label="Milestone added"
        sub="Silver Slugger · 2019"
        tone="emerald"
        icon={Trophy}
        positionClass="-top-3 right-0 sm:right-4"
      />

      <div className="rounded-3xl bg-neutral-950 ring-1 ring-white/10 p-5 sm:p-6">
        <p className="text-[10px] uppercase tracking-[0.32em] font-bold text-blue-300">The Journey</p>
        <h4 className="mt-1 font-display uppercase text-white text-2xl sm:text-3xl leading-none tracking-tight">
          Career Timeline
        </h4>

        <div className="mt-6 relative pl-6">
          {/* Vertical line */}
          <div aria-hidden className="absolute left-[7px] top-2 bottom-2 w-px bg-gradient-to-b from-emerald-400/40 via-emerald-400/20 to-transparent" />

          <div className="space-y-3">
            {milestones.map((m) => (
              <div key={m.year} className="relative">
                {/* Dot */}
                <div className="absolute -left-6 top-4 w-3.5 h-3.5 rounded-full bg-emerald-400 ring-4 ring-black shadow-[0_0_12px_rgba(52,211,153,0.6)]" />
                <div className="rounded-2xl ring-1 ring-white/10 bg-white/[0.02] p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full ring-1 ring-emerald-400/40 bg-emerald-400/10 text-emerald-300 text-[9px] uppercase tracking-widest font-bold">
                      <Sparkles className="w-2.5 h-2.5" />
                      Milestone
                    </span>
                    <span className="text-white/50 text-[11px] uppercase tracking-widest">{m.year}</span>
                    {m.tag && (
                      <span className="text-white/40 text-[10px] uppercase tracking-widest">· {m.tag}</span>
                    )}
                  </div>
                  <p className="mt-2 font-display uppercase text-white text-base sm:text-lg leading-tight tracking-tight">
                    {m.title}
                  </p>
                  {m.stats && (
                    <div className="mt-3 grid grid-cols-3 gap-2">
                      {m.stats.map((s) => (
                        <div key={s.k} className="rounded-lg ring-1 ring-white/10 bg-white/[0.02] px-2 py-1.5">
                          <p className="font-display text-white text-base leading-none">{s.v}</p>
                          <p className="mt-0.5 text-[8px] uppercase tracking-widest text-white/40">{s.k}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── VISUAL 03 · Bio-link builder ─────────────────────────────
 * Mimics the FLYT Athletes link builder screenshot: cover image,
 * FLYT logo circle, "FLYT Athletes" title, link tile rows with
 * FLYTHOUSE / HUDL / FLYTSTUDIOS chips, then a video preview. */
function BioLinkBuilderVisual({ athlete }) {
  // iter228 · Toggle between athlete link vs agent link so visitors
  // see the same builder produces two different products. The
  // agent variant additionally shows a ROSTER section (the athletes
  // signed to the agency), which athlete links don't have.
  const [mode, setMode] = useState('athlete');
  // iter232 · Local demo state for the on-landing SocialLinksDots
  // preview — Instagram is pre-filled so visitors immediately see
  // the "filled" state (green check) next to the empty +'s.
  const [demoSocials, setDemoSocials] = useState({
    instagram: '@jaxon',
    social_links: { tiktok: '', youtube: '', twitter: '', facebook: '', linkedin: '', website: '' },
  });
  const isAgent = mode === 'agent';

  const athleteLinks = [
    { label: 'Jaxon', url: 'flythouse.com/network/jaxon', chip: 'FLYTHOUSE' },
    { label: 'Highlights', url: 'hudl.com/v/jaxon-highlights', chip: 'HUDL' },
    { label: 'FLYT Studios', url: 'flytstudios.com', chip: 'FLYTSTUDIOS' },
  ];
  const agentLinks = [
    { label: 'FLYT Athletes', url: 'flythouse.com/agency/flyt-athletes', chip: 'FLYTHOUSE' },
    { label: 'Book Us', url: 'flytathletes.com/book', chip: 'WEBSITE' },
    { label: 'Press Inquiries', url: 'mailto:press@flytathletes.com', chip: 'CONTACT' },
  ];
  const links = isAgent ? agentLinks : athleteLinks;
  const heroTitle = isAgent ? 'FLYT Athletes' : 'Jaxon · FLYT Studios';
  const heroUrl = isAgent ? 'flythouse.com/l/flyt-athletes' : 'flythouse.com/l/jaxon';

  const videos = [
    // iter556 · Two videos now demonstrate BOTH source types the FLYT
    // ID Builder supports so viewers see the multi-provider story
    // immediately. Jaxon's Hudl highlight leads with his real photo
    // and title; the second video is a YouTube example for variety.
    // iter559 · The second video now shows a football-field thumbnail
    // instead of a darkened duplicate of Jaxon's headshot — visually
    // distinct so the two source-types read as separate content, not
    // the same clip twice.
    { title: 'Jaxon story', channel: 'Westlake Huskies', source: 'HUDL', badge: 'Hudl highlights', useAthletePhoto: true },
    { title: 'Justin Herbert Is Out', channel: 'Another Sports Channel', source: 'YouTube', thumbUrl: 'https://images.unsplash.com/photo-1566577739112-5180d4bf9390?w=600&q=70&auto=format&fit=crop' },
  ];
  const roster = [
    { name: 'Chris', team: 'FLYT Studios · TE' },
    { name: 'Josiah', team: 'Texas · WR' },
    { name: 'Isabelle', team: 'Texas · Track' },
  ];
  const addBlocksAthlete = [
    { icon: Video, label: 'Videos', hint: 'YouTube · Hudl · Vimeo' },
    { icon: BarChart3, label: 'MaxPreps', hint: 'Verified season stats' },
    { icon: Bookmark, label: 'Articles', hint: 'Press · features' },
    { icon: Sparkles, label: 'Milestones', hint: 'Awards · signings' },
  ];
  const addBlocksAgent = [
    { icon: Users, label: 'Roster', hint: 'Every signed athlete' },
    { icon: Video, label: 'Videos', hint: 'YouTube · Hudl · Vimeo' },
    { icon: DollarSign, label: 'Payments', hint: 'Stripe payouts inline' },
    { icon: Send, label: 'Bulk Nudges', hint: 'Message the whole book' },
  ];
  const addBlocks = isAgent ? addBlocksAgent : addBlocksAthlete;

  return (
    <div className="relative w-full">
      <NotifBubble
        label="Link tapped"
        sub="+12 clicks today"
        tone="fuchsia"
        icon={ArrowUpRight}
        positionClass="-top-3 right-2"
      />

      {/* Persona toggle — pill switch above the phone */}
      <div className="flex justify-center mb-4">
        <div className="inline-flex items-center gap-1 rounded-full bg-white/[0.04] ring-1 ring-white/10 p-1" data-testid="link-builder-toggle">
          {[
            { key: 'athlete', label: 'FLYTID', icon: 'wings' },
            { key: 'agent', label: 'Agent link', icon: Briefcase },
          ].map((opt) => {
            const Icon = opt.icon;
            const active = mode === opt.key;
            return (
              <button
                type="button"
                key={opt.key}
                onClick={() => setMode(opt.key)}
                data-testid={`link-builder-toggle-${opt.key}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] uppercase tracking-widest font-bold transition ${active ? 'bg-white text-black' : 'text-white/60 hover:text-white'}`}
              >
                {Icon === 'wings' ? (
                  <FlytWings className="w-4 h-3.5" />
                ) : (
                  <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                )}
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
        {/* Live phone editor — 3 cols on desktop */}
        <div className="md:col-span-3 max-w-[360px] w-full mx-auto md:mx-0">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-emerald-300">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Live Preview
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/40 text-emerald-200 text-[10px] uppercase tracking-widest font-bold">
              <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
              Done
            </span>
          </div>

          <div className="rounded-[32px] bg-black ring-1 ring-white/10 overflow-hidden shadow-[0_24px_48px_-12px_rgba(0,0,0,0.6)]">
            <div className="relative h-20">
              {athlete?.cover_image || athlete?.photo ? (
                <img src={athlete.cover_image || athlete.photo} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(0.55)' }} />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-black to-fuchsia-900" />
              )}
              <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-transparent to-black" />
              <div className="absolute inset-x-0 bottom-2 text-center">
                <p className="font-display uppercase text-white text-base tracking-tight">{heroTitle}</p>
                <p className="text-white/60 text-[10px]">{heroUrl}</p>
              </div>
            </div>

            <div className="p-3 space-y-1.5">
              {links.map((l) => (
                <div key={l.label} className="rounded-2xl ring-1 ring-white/10 bg-white/[0.03] px-2.5 py-2 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md bg-emerald-500/20 ring-1 ring-emerald-400/30 flex items-center justify-center shrink-0">
                    <ArrowUpRight className="w-3 h-3 text-emerald-300" strokeWidth={2.5} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] text-white font-bold truncate">{l.label}</p>
                    <p className="text-[8px] uppercase tracking-widest text-white/40 truncate">{l.url}</p>
                  </div>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-emerald-500/25 ring-1 ring-emerald-400/40 text-emerald-200 text-[7px] uppercase tracking-widest font-bold shrink-0">
                    {l.chip}
                  </span>
                  <span className="text-white/30 text-[10px] font-black shrink-0">⋮⋮</span>
                </div>
              ))}
            </div>

            <div className="px-3 pb-3 space-y-1.5">
              <p className="text-[9px] uppercase tracking-widest text-white/40 font-bold inline-flex items-center gap-1">
                <Video className="w-2.5 h-2.5" /> Videos
              </p>
              {videos.map((v, i) => (
                <div key={i} className="rounded-xl overflow-hidden ring-1 ring-white/10 bg-neutral-900 relative">
                  <div className="relative aspect-video">
                    {/* iter556 · Jaxon's Hudl highlight leads with his
                        real athlete photo so recruiters/brands see a
                        person, not a placeholder. iter559 · Other
                        videos use their own `thumbUrl` (e.g. a
                        football-field stock) so they don't duplicate
                        Jaxon's headshot as a darkened backdrop. */}
                    {v.useAthletePhoto && athlete?.photo ? (
                      <img src={athlete.photo} alt="Jaxon highlight thumbnail" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(0.7)' }} />
                    ) : v.thumbUrl ? (
                      <img src={v.thumbUrl} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'brightness(0.85)' }} />
                    ) : null}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className={`w-10 h-8 rounded-md ${v.source === 'HUDL' ? 'bg-orange-500' : 'bg-red-600'} flex items-center justify-center shadow-lg`}>
                        <Play className="w-4 h-4 fill-white text-white" />
                      </div>
                    </div>
                    <div className="absolute top-1.5 left-1.5 flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-white/20 ring-1 ring-white/30" />
                      <div className="max-w-[180px]">
                        <p className="text-white text-[10px] font-bold truncate">{v.title}</p>
                        <p className="text-white/70 text-[8px] truncate">{v.channel}</p>
                      </div>
                    </div>
                    <span className="absolute top-1.5 right-1.5 text-white/40 text-[10px] font-black">⋮⋮</span>
                    {/* iter556 · Source pill on the bottom of each
                        video (Hudl / YouTube). Signals the FLYT ID™
                        Builder aggregates from multiple video
                        providers, not just one. */}
                    <span
                      className={`absolute bottom-1.5 right-1.5 rounded-full px-2 py-0.5 text-[8px] font-black uppercase tracking-widest ring-1 ${
                        v.source === 'HUDL'
                          ? 'bg-orange-500/90 text-white ring-orange-300/60'
                          : 'bg-red-600/90 text-white ring-red-300/60'
                      }`}
                    >
                      {v.badge || v.source}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* ROSTER — only rendered for the agent variant. Athlete
                links don't have this section on Flythouse; hiding it
                signals the two products are meaningfully different. */}
            {isAgent && (
              <div className="px-3 pb-3" data-testid="link-builder-agent-roster">
                <p className="text-[9px] uppercase tracking-widest text-white/40 font-bold inline-flex items-center gap-1 mb-1.5">
                  <Users className="w-2.5 h-2.5" /> Roster · {roster.length}
                </p>
                <div className="space-y-1.5">
                  {roster.map((r, i) => (
                    <div key={i} className="rounded-xl ring-1 ring-white/10 bg-white/[0.02] px-2.5 py-2 flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${['from-blue-600 to-fuchsia-500', 'from-emerald-500 to-cyan-500', 'from-amber-400 to-red-500'][i % 3]} shrink-0`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-[11px] font-bold truncate">{r.name}</p>
                        <p className="text-white/50 text-[9px] uppercase tracking-widest truncate">{r.team}</p>
                      </div>
                      <span className="text-white/30 text-[10px] font-black shrink-0">⋮⋮</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="relative px-3 pb-4">
              <div className="flex items-center justify-center">
                <span className="inline-flex items-center gap-1 text-emerald-300 text-[10px] uppercase tracking-widest font-bold">
                  <FlytWings className="w-3 h-3" /> FLYT ID™
                </span>
              </div>
              <div className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-emerald-400 flex items-center justify-center shadow-lg">
                <span className="text-black text-xl font-black leading-none pb-0.5">+</span>
              </div>
            </div>
          </div>
        </div>

        {/* Builder side panel — switches list to match the persona */}
        <div className="md:col-span-2 space-y-4">
          {/* iter232 · Same SocialLinksDots component that ships in
              the real IntegrateBuilder / AgentEditProfileModal so
              visitors see the exact one-tap-add UX on the landing. */}
          <SocialLinksDots
            value={demoSocials}
            onChange={setDemoSocials}
            label="Social · one-tap add"
          />
          <div className="rounded-2xl ring-1 ring-white/10 bg-white/[0.03] p-4">
            <p className="text-[10px] uppercase tracking-[0.28em] font-bold text-blue-300">
              {isAgent ? 'Add to your agency page' : 'Add to your profile'}
            </p>
            <p className="mt-1 text-[11px] text-white/50 leading-tight">
              Turn on any block. Live-preview updates as you go.
            </p>
            <div className="mt-4 space-y-2">
              {addBlocks.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.label} className="rounded-xl ring-1 ring-white/10 bg-black/40 px-3 py-2 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-white/[0.06] ring-1 ring-white/10 flex items-center justify-center shrink-0">
                      <Icon className="w-4 h-4 text-blue-300" strokeWidth={2} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] text-white font-bold truncate">Add {b.label}</p>
                      <p className="text-[9px] uppercase tracking-widest text-white/40 truncate">{b.hint}</p>
                    </div>
                    <div className="w-8 h-4 rounded-full bg-emerald-500/30 ring-1 ring-emerald-400/50 relative shrink-0">
                      <div className="absolute right-0.5 top-0.5 w-3 h-3 rounded-full bg-emerald-300" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── SECTION CARD wrapper ─────────────────────────────────── */
function SectionCard({ number, eyebrow, title, body, cta, ctaTo, ctaTestid, ctaOnClick, ctaSecondary, ctaSecondaryTo, ctaSecondaryTestid, children }) {
  const ctaBtnClass = "group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black text-[11px] font-black uppercase tracking-[0.24em] hover:bg-blue-300 transition";
  return (
    <div
      className="relative rounded-[28px] sm:rounded-[36px] overflow-hidden ring-1 ring-white/10 bg-neutral-950/70 backdrop-blur-xl"
      data-testid={`landing-section-${number}`}
    >
      <div className="relative p-4 sm:p-8 bg-gradient-to-br from-neutral-900 via-black to-neutral-950">
        {children}
      </div>
      <div className="p-5 sm:p-7">
        <p className="text-[11px] uppercase tracking-[0.32em] font-bold text-white/40">{number}</p>
        {eyebrow && (
          <p className="mt-2 text-[11px] uppercase tracking-[0.32em] font-bold text-blue-300">{eyebrow}</p>
        )}
        <h3 className="mt-2 font-display uppercase tracking-tight text-white text-xl sm:text-3xl lg:text-4xl leading-[1.02]">
          {title}
        </h3>
        <p className="mt-3 text-white/70 text-sm sm:text-base leading-relaxed max-w-xl">{body}</p>
        {(cta || ctaSecondary) && (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            {cta && (
              ctaOnClick ? (
                <button
                  type="button"
                  onClick={ctaOnClick}
                  data-testid={ctaTestid}
                  className={ctaBtnClass}
                >
                  {cta}
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition" />
                </button>
              ) : (
                <Link
                  to={ctaTo}
                  data-testid={ctaTestid}
                  className={ctaBtnClass}
                >
                  {cta}
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition" />
                </Link>
              )
            )}
            {ctaSecondary && (
              <Link
                to={ctaSecondaryTo}
                data-testid={ctaSecondaryTestid}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full ring-1 ring-white/20 bg-white/[0.04] text-white text-[11px] font-bold uppercase tracking-[0.24em] hover:bg-white/[0.1] transition"
              >
                {ctaSecondary}
              </Link>
            )}
            {/* iter616 · Always pair the primary CTA with an App Store
                badge so any "Make mine now / See a live timeline" click
                has an equally-weighted "Download the app" path right
                beside it. Auto-hides inside the native iOS shell. */}
            <AppStoreBadge size="sm" testid={`${ctaTestid || 'landing'}-app-store`} />
          </div>
        )}
      </div>
    </div>
  );
}

/* ── PERSONA EXPLORER — tabbed feature explanation ─────────── */
const PERSONA_TABS = [
  {
    key: 'athlete',
    label: 'Athlete',
    icon: Trophy,
    accent: 'text-blue-300',
    dot: 'bg-blue-400',
    headline: 'Build a career that outlives your career.',
    bullets: [
      'One permanent URL — flythouse.com/yourname — for every bio and DM.',
      'Highlights, stats, awards, and offers on one clean timeline.',
      'Verified profile so recruiters and brands trust what they see.',
      'Connect directly to your agent + school and share updates in one place.',
      'Live NIL deal tracking with active partners on your profile.',
      'Direct DMs from real agents and brand managers — no bots.',
    ],
    ctaLabel: 'Log in as an athlete',
    ctaTo: '/for-athletes',
  },
  {
    key: 'agent',
    label: 'Agent',
    icon: Briefcase,
    accent: 'text-emerald-300',
    dot: 'bg-emerald-400',
    headline: 'Run your entire roster in one dashboard.',
    bullets: [
      'Link every athlete on your book to your agency page.',
      'Send unified nudges + campaign requests without a spreadsheet.',
      'Send payments and NIL splits directly to athletes via Stripe.',
      'Track brand outreach + acceptance in one shared inbox.',
      'FLYT Studios production ROI widget for every campaign spend.',
      'Compliance + moderation queue for anything published on-brand.',
    ],
    ctaLabel: 'Log in as an agent',
    ctaTo: '/for-agents',
  },
  // iter583 · Brand persona hidden from the public home landing block
  // on Jay's request — kept as commented-out entry so it's a one-line
  // restore when brand-side pitches go live.
  // { key: 'brand', label: 'Brand', icon: Sparkles, … ctaTo: '/for-brands' },
  {
    key: 'program',
    label: 'Program',
    icon: GraduationCap,
    accent: 'text-amber-300',
    dot: 'bg-amber-400',
    headline: 'Give every athlete their own story page.',
    bullets: [
      'Roll out branded profiles across your entire roster in minutes.',
      'Keep alumni connected to the program forever.',
      'Let recruiters find you the same way brands do.',
      'Bulk import via CSV or paste from a directory.',
      'Program analytics — profile views, deal volume, alumni activity.',
    ],
    ctaLabel: 'Log in as a program',
    ctaTo: '/for-programs',
  },
];

function PersonaExplorer() {
  const { setLoginOpen } = useUserAuth();
  return (
    <div className="rounded-[28px] sm:rounded-[36px] overflow-hidden ring-1 ring-white/10 bg-neutral-950/70 backdrop-blur-xl p-6 sm:p-8">
      <p className="text-[11px] uppercase tracking-[0.32em] font-bold text-white/40">04</p>
      <p className="mt-2 text-[11px] uppercase tracking-[0.32em] font-bold text-blue-300">Pick your door</p>
      <h3 className="mt-2 font-display uppercase tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl leading-[1.02]">
        See what you get.
      </h3>

      <Tabs defaultValue="athlete" className="mt-6">
        <TabsList className="w-full flex overflow-x-auto scrollbar-hide gap-1 bg-white/[0.04] ring-1 ring-white/10 rounded-full p-1 h-auto justify-start">
          {PERSONA_TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger
                key={t.key}
                value={t.key}
                data-testid={`persona-tab-${t.key}`}
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[10px] uppercase tracking-widest font-bold text-white/60 data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow-none shrink-0"
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
        {PERSONA_TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-6">
            <p className={`inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold ${t.accent}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
              For {t.label}s
            </p>
            <h4 className="mt-3 font-display uppercase text-white text-xl sm:text-2xl leading-tight tracking-tight">
              {t.headline}
            </h4>
            <ul className="mt-5 space-y-2.5">
              {t.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${t.accent}`} strokeWidth={2} />
                  <p className="text-[14px] text-white/80 leading-snug">{b}</p>
                </li>
              ))}
            </ul>
            {t.key === 'agent' && (
              <>
                {/* Mini agent-console mockup — 3-row roster + payment tile */}
                <div className="mt-5 rounded-2xl ring-1 ring-white/10 bg-black/40 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-white/50">Your roster · 3 of 42</p>
                    <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-300">Live</span>
                  </div>
                  <div className="space-y-1.5">
                    {[
                      { n: 'Chris', pos: 'TE · Dallas', deals: 3 },
                      { n: 'Ozzie Albies', pos: '2B · Braves', deals: 5 },
                      { n: 'Devon Wilson', pos: 'WR · Duncanville', deals: 2 },
                    ].map((a, i) => (
                      <div key={i} className="rounded-lg ring-1 ring-white/10 bg-white/[0.02] px-3 py-2 flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${['from-blue-500 to-fuchsia-500','from-amber-400 to-red-500','from-emerald-400 to-cyan-500'][i]} flex items-center justify-center text-white text-xs font-black`}>
                          {a.n[0]}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-[12px] font-bold truncate">{a.n}</p>
                          <p className="text-white/50 text-[9px] uppercase tracking-widest">{a.pos}</p>
                        </div>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/30 text-emerald-200 text-[9px] uppercase tracking-widest font-bold shrink-0">
                          {a.deals} deals
                        </span>
                      </div>
                    ))}
                  </div>
                  {/* Payment strip */}
                  <div className="mt-3 rounded-xl ring-1 ring-emerald-400/30 bg-emerald-500/10 px-3 py-2.5 flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/25 ring-1 ring-emerald-400/40 flex items-center justify-center shrink-0">
                      <DollarSign className="w-4 h-4 text-emerald-200" strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-[12px] font-bold truncate">Send $4,200 to Chris</p>
                      <p className="text-emerald-200 text-[9px] uppercase tracking-widest">Stripe · Instant</p>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-200 shrink-0">Send →</span>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {[
                    { icon: Users, label: 'Roster CRM' },
                    { icon: DollarSign, label: 'Stripe payouts' },
                    { icon: Send, label: 'Bulk nudges' },
                    { icon: BadgeCheck, label: 'Verified only' },
                  ].map((c) => {
                    const Icon = c.icon;
                    return (
                      <div key={c.label} className="rounded-xl ring-1 ring-white/10 bg-white/[0.03] p-3 flex items-center gap-2">
                        <Icon className="w-4 h-4 text-emerald-300 shrink-0" strokeWidth={2} />
                        <p className="text-[12px] text-white font-bold">{c.label}</p>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {t.key === 'athlete' && (
              <div className="mt-5 rounded-2xl ring-1 ring-white/10 bg-black/40 p-4">
                <p className="text-[10px] uppercase tracking-widest font-bold text-white/50 mb-3">Your connections</p>
                <div className="space-y-1.5">
                  {[
                    { icon: Briefcase, label: 'FLYT Athletes', sub: 'Your agent · signed', tone: 'text-emerald-300 bg-emerald-500/15 ring-emerald-400/30' },
                    { icon: GraduationCap, label: 'University of Texas', sub: 'Your program · committed', tone: 'text-amber-300 bg-amber-500/15 ring-amber-400/30' },
                    { icon: Sparkles, label: 'Gatorade', sub: 'Brand deal · active', tone: 'text-fuchsia-300 bg-fuchsia-500/15 ring-fuchsia-400/30' },
                  ].map((c, i) => {
                    const Icon = c.icon;
                    return (
                      <div key={i} className={`rounded-lg ring-1 px-3 py-2 flex items-center gap-2.5 ${c.tone}`}>
                        <Icon className="w-4 h-4 shrink-0" strokeWidth={2} />
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-[12px] font-bold truncate">{c.label}</p>
                          <p className="text-white/60 text-[9px] uppercase tracking-widest truncate">{c.sub}</p>
                        </div>
                        <CheckCircle2 className="w-4 h-4 shrink-0" strokeWidth={2} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {t.key === 'program' && (
              // iter229c · Real school-dashboard preview — mirrors the
              // actual `/school/dashboard` (Command Center) UI. Sidebar
              // + KPI tiles + Needs Attention + Recent Activity. Drops
              // the old bright gradient grid entirely.
              <div className="mt-5 rounded-2xl ring-1 ring-white/10 bg-black/50 overflow-hidden" data-testid="landing-program-preview">
                {/* iter559 · Mobile fix — grid collapses to a single
                    column below sm so the hidden 92px sidebar doesn't
                    reserve dead space and squeeze the KPI/activity
                    tiles off the edge. Sidebar re-appears at sm+. */}
                <div className="grid grid-cols-1 sm:grid-cols-[92px_1fr] min-h-[320px]">
                  {/* Sidebar */}
                  <aside className="border-r border-white/[0.06] bg-white/[0.015] py-3 px-2 hidden sm:block">
                    <div className="flex items-center gap-1.5 px-1.5 pb-2 mb-2 border-b border-white/[0.06]">
                      <div className="w-6 h-6 rounded-md bg-amber-500/20 ring-1 ring-amber-400/40 flex items-center justify-center">
                        <GraduationCap className="w-3 h-3 text-amber-300" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[8px] font-display uppercase leading-tight text-white truncate">SMU</p>
                        <p className="text-[7px] uppercase tracking-wider text-white/40 truncate">Athletic Dept</p>
                      </div>
                    </div>
                    <ul className="space-y-0.5">
                      {[
                        { icon: LayoutGrid, label: 'Dashboard', active: true },
                        { icon: Users, label: 'Athletes' },
                        { icon: Building2, label: 'Departments' },
                        { icon: Bookmark, label: 'Stories' },
                        { icon: ImageIcon, label: 'Media' },
                        { icon: Sparkles, label: 'Brands' },
                        { icon: BarChart3, label: 'Analytics' },
                      ].map((s) => {
                        const Icon = s.icon;
                        return (
                          <li
                            key={s.label}
                            className={`flex items-center gap-1.5 rounded-md px-1.5 py-1 ${
                              s.active
                                ? 'bg-amber-500/15 ring-1 ring-amber-400/30 text-amber-100'
                                : 'text-white/50'
                            }`}
                          >
                            <Icon className="w-2.5 h-2.5 shrink-0" />
                            <span className="text-[8px] font-bold uppercase tracking-wider truncate">
                              {s.label}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </aside>

                  {/* Main pane */}
                  <div className="p-3 sm:p-4">
                    {/* Command Center header */}
                    <p className="text-[8px] uppercase tracking-[0.32em] font-bold text-white/40">Command Center</p>
                    <p className="mt-0.5 font-display uppercase text-white text-lg sm:text-xl leading-none tracking-tight">
                      Welcome, Coach.
                    </p>
                    <p className="mt-1 text-white/50 text-[10px]">SMU Mustangs · Organization Owner</p>

                    {/* Quick actions */}
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {[
                        { icon: UserPlus, label: 'Add Athlete', primary: true },
                        { icon: Upload, label: 'Upload Roster' },
                        { icon: Send, label: 'Invite Staff' },
                      ].map((b) => {
                        const Icon = b.icon;
                        return (
                          <span
                            key={b.label}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[8px] uppercase tracking-widest font-bold ring-1 ${
                              b.primary
                                ? 'bg-amber-500 ring-amber-400/60 text-black'
                                : 'bg-white/5 ring-white/10 text-white/70'
                            }`}
                          >
                            <Icon className="w-2.5 h-2.5" />
                            {b.label}
                          </span>
                        );
                      })}
                    </div>

                    {/* KPI tiles — dark, subtle, real */}
                    <div className="mt-3 grid grid-cols-4 gap-1.5">
                      {[
                        { label: 'Active', value: '187', sub: '12 verified', icon: Users },
                        { label: 'Dept', value: '4', sub: 'football+3', icon: Building2 },
                        { label: 'Staff', value: '9', sub: 'all active', icon: BadgeCheck },
                        { label: 'Profiles', value: '84%', sub: '30 to go', icon: CheckCircle2 },
                      ].map((k) => {
                        const Icon = k.icon;
                        return (
                          <div
                            key={k.label}
                            className="rounded-lg ring-1 ring-white/8 bg-white/[0.02] px-2 py-1.5"
                          >
                            <div className="flex items-center justify-between">
                              <p className="text-[7px] uppercase tracking-widest font-bold text-white/45">
                                {k.label}
                              </p>
                              <Icon className="w-2.5 h-2.5 text-white/25" />
                            </div>
                            <p className="mt-0.5 font-display text-white text-base leading-none">
                              {k.value}
                            </p>
                            <p className="mt-0.5 text-[7px] uppercase tracking-widest text-white/35 truncate">
                              {k.sub}
                            </p>
                          </div>
                        );
                      })}
                    </div>

                    {/* Needs Attention + Recent Activity */}
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_1.2fr] gap-2">
                      <div className="rounded-lg ring-1 ring-white/8 bg-white/[0.02] p-2">
                        <p className="flex items-center gap-1 text-[8px] uppercase tracking-widest font-bold text-amber-300/90">
                          <AlertCircle className="w-2.5 h-2.5" />
                          Needs Attention
                        </p>
                        <p className="mt-1 text-white/80 text-[10px] leading-snug">
                          30 profiles missing bio or photo
                        </p>
                        <p className="mt-1 text-amber-300 text-[8px] uppercase tracking-widest font-bold">
                          Review athletes ↗
                        </p>
                      </div>
                      <div className="rounded-lg ring-1 ring-white/8 bg-white/[0.02] p-2">
                        <p className="flex items-center gap-1 text-[8px] uppercase tracking-widest font-bold text-white/45">
                          <Bell className="w-2.5 h-2.5" />
                          Recent Activity
                        </p>
                        <ul className="mt-1 space-y-1">
                          {[
                            { n: 'Dante Wesley II', pos: 'Football · CB' },
                            { n: 'IV Webb', pos: 'Football · LB' },
                            { n: "La'Modrick Spencer", pos: 'Football · CB' },
                          ].map((a) => (
                            <li key={a.n} className="flex items-center gap-1.5">
                              <div className="w-3.5 h-3.5 rounded-full bg-white/10 ring-1 ring-white/15 flex items-center justify-center shrink-0">
                                <Users className="w-2 h-2 text-white/60" />
                              </div>
                              <p className="text-white text-[9px] font-bold truncate flex-1 min-w-0">
                                Athlete added · {a.n}
                              </p>
                              <p className="text-white/40 text-[7px] uppercase tracking-widest shrink-0 hidden sm:block">
                                {a.pos}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <Link
              to={t.ctaTo}
              onClick={(e) => { e.preventDefault(); setLoginOpen(true); }}
              data-testid={`persona-cta-${t.key}`}
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black text-[11px] font-black uppercase tracking-[0.24em] hover:bg-blue-300 transition"
            >
              {t.ctaLabel}
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

/**
 * iter555 · NetworkProfileVisual — mirrors the live network profile
 * layout (see `/network/:slug`) used on flythouse.com's athlete pages.
 * Renders on the logged-out landing so visitors see exactly what a
 * FLYT Network profile looks like using Jaxon's real data.
 *
 * Deliberately a snapshot copy (not a shared component) so this
 * marketing surface stays fixed if the real profile page evolves —
 * we don't want a nightly refactor to break the landing page's
 * hero visual right before a fundraise / press cycle.
 */
const NetworkProfileVisual = ({ athlete }) => {
  const a = athlete || {};
  const firstName = (a.name || 'Jaxon').split(/\s+/)[0].toUpperCase();
  const photo = a.photo || 'https://customer-assets.emergentagent.com/job_jay-pernell-sports/artifacts/z7u9j3pl_white%20wings_edited.png';
  const school = a.school_name || a.school || 'Westlake';
  const team = a.team || 'Huskies';
  const position = a.position || 'TE';
  const level = a.stage === 'college' ? 'NCAA' : a.stage === 'pro' ? 'NFL' : 'HS';
  const stats = a.stats || {};

  return (
    <div className="relative rounded-2xl bg-black ring-1 ring-white/10 overflow-hidden">
      {/* iter564 · Photo positioned so the athlete anchors to the RIGHT
          of the container. Because Jaxon's real portrait has him on
          the LEFT of the raw image with black backdrop on the RIGHT,
          `object-position: 0% 0%` (left-top) shows the LEFT crop of
          the raw image scaled up — pushing his figure into the
          right-center of the container. Any athlete photo where the
          subject sits toward the left of the raw frame will now
          right-anchor cleanly. Gradient overlays sit above so the
          left edge fades into the content column. */}
      <img
        src={photo}
        alt=""
        aria-hidden="true"
        className="absolute right-0 top-0 w-[62%] sm:w-[55%] h-[62%] sm:h-[75%] object-cover"
        style={{ objectPosition: '20% 0%', opacity: 0.98 }}
      />
      <div
        className="absolute right-0 top-0 w-[62%] sm:w-[55%] h-[62%] sm:h-[75%] pointer-events-none"
        style={{
          background:
            'linear-gradient(90deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.55) 18%, rgba(0,0,0,0.15) 45%, rgba(0,0,0,0) 70%), linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.9) 100%)',
        }}
      />
      <div className="relative p-4 sm:p-6 min-h-[520px] sm:min-h-[600px] flex flex-col">
        {/* iter563 · TOP section — name / pills / follow / socials.
            Constrained to the LEFT half on mobile so the photo behind
            it stays visible; wider on desktop where space is plenty. */}
        <div className="max-w-[52%] sm:max-w-[62%]">
          <p className="text-[9px] uppercase tracking-[0.32em] font-bold text-white/40">FLYT ID™</p>
          <div className="flex items-center gap-2 mt-1">
            <h4 className="font-display uppercase text-white text-3xl sm:text-5xl leading-none tracking-tight">{firstName}</h4>
            <span className="text-[9px] font-black uppercase tracking-widest text-amber-300 ring-1 ring-amber-300/60 rounded-full px-1.5 py-0.5">{level}</span>
          </div>
          <span className="inline-flex mt-1.5 items-center justify-center w-4 h-4 rounded-full bg-blue-500">
            <svg viewBox="0 0 20 20" className="w-2.5 h-2.5 text-white fill-current"><path d="M8 12L4.5 8.5l-1 1L8 14l9-9-1-1z"/></svg>
          </span>
          <p className="mt-2 text-blue-300 font-bold text-[11px] sm:text-sm uppercase tracking-widest">{position} · {school}</p>
          <p className="text-white/60 text-xs sm:text-sm mt-0.5">{team}</p>

          <div className="flex flex-wrap gap-1.5 mt-3">
            {[position, '#1', 'Class of ’26'].map((c) => (
              <span key={c} className="rounded-full ring-1 ring-white/15 bg-white/[0.03] text-white/80 text-[10px] font-bold px-2 py-0.5">{c}</span>
            ))}
          </div>

          <div className="flex flex-wrap gap-1.5 mt-2.5">
            {['Follow', 'Get Updates', 'Share'].map((label) => (
              <span key={label} className="inline-flex items-center gap-1 rounded-full ring-1 ring-white/15 bg-white/[0.03] text-white/85 text-[10px] font-bold px-2.5 py-1">
                {label}
              </span>
            ))}
          </div>

          {/* iter557 · Add-to-lock-screen CTA */}
          <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-full ring-1 ring-emerald-400/50 bg-emerald-400/[0.03] text-emerald-300 text-[10px] font-black uppercase tracking-widest px-2.5 py-1">
            <svg viewBox="0 0 20 20" className="w-3 h-3 fill-current"><path d="M6 2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2zm0 2v12h8V4H6z"/></svg>
            Add to Lock Screen
          </div>

          {/* iter557 · Socials row */}
          <div className="flex items-center gap-2 mt-3">
            {[
              { color: 'ring-fuchsia-400/60', bg: 'bg-fuchsia-500/10', label: '♪' },
              { color: 'ring-red-500/60', bg: 'bg-red-500/10', label: '▶' },
              { color: 'ring-white/40', bg: 'bg-white/[0.05]', label: '𝕏' },
              { color: 'ring-pink-500/60', bg: 'bg-pink-500/10', label: '◉' },
            ].map((s, i) => (
              <span key={i} className={`w-6 h-6 rounded-full ring-1 ${s.color} ${s.bg} flex items-center justify-center text-white/80 text-[10px]`}>
                {s.label}
              </span>
            ))}
          </div>
        </div>

        {/* iter563 · BOTTOM section — stats + partners + business row.
            Full width, mt-auto, sits below the photo's vertical crop
            (see gradient 60%→bottom fade above). Solid dark backdrop
            so nothing behind bleeds through. */}
        <div className="relative mt-auto pt-4">
          {/* Stats bar — 6 metrics */}
          <div className="flex items-center justify-between bg-black/70 ring-1 ring-white/10 rounded-xl px-2.5 py-2 text-white backdrop-blur-sm">
            {[
              { v: stats.height || "6'6", l: 'HT' },
              { v: stats.weight || '250', l: 'WT' },
              { v: stats.forty || '4.5', l: '40' },
              { v: stats.gpa || '3.8', l: 'GPA' },
              { v: stats.sat || '14', l: 'SAT' },
              { v: stats.act || '31', l: 'ACT' },
            ].map((s) => (
              <div key={s.l} className="text-center min-w-0 px-0.5">
                <p className="font-display text-[13px] sm:text-sm leading-none">{s.v}</p>
                <p className="text-[7px] uppercase tracking-widest text-white/50 mt-0.5">{s.l}</p>
              </div>
            ))}
          </div>

          {/* iter557 · Partner integrations row — Hudl / MaxPreps / Opendorse */}
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            {[
              { name: 'HUDL', sub: 'Film', tone: 'text-orange-400' },
              { name: 'MAXPREPS', sub: 'Stats', tone: 'text-red-400' },
              { name: 'OPENDORSE', sub: 'NIL', tone: 'text-emerald-400' },
            ].map((p) => (
              <div key={p.name} className="rounded-lg ring-1 ring-white/10 bg-black/70 backdrop-blur-sm px-1.5 py-1.5 min-w-0">
                <p className={`text-[8px] font-black uppercase tracking-widest truncate ${p.tone}`}>{p.name}</p>
                <p className="text-white/60 text-[8px] uppercase tracking-widest mt-0.5 truncate">{p.sub}</p>
              </div>
            ))}
          </div>

          {/* Business inquiries CTA */}
          <div className="mt-2 flex items-center justify-between gap-2 rounded-xl bg-black/70 backdrop-blur-sm p-1.5 ring-1 ring-white/10">
            <div className="flex items-center gap-1 pl-1">
              <span className="text-[8px] uppercase tracking-widest text-white/40 font-bold mr-1 hidden sm:inline">Explore</span>
              {['FLYT', 'FA', 'WE'].map((c) => (
                <span key={c} className="w-5 h-5 sm:w-6 sm:h-6 rounded-full ring-1 ring-white/15 bg-white/[0.04] text-white/70 text-[8px] font-black flex items-center justify-center">
                  {c}
                </span>
              ))}
            </div>
            <span className="inline-flex items-center justify-center gap-1 rounded-full bg-blue-500 text-black text-[10px] font-black px-3 py-1.5 whitespace-nowrap">
              Business inquiries
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};


/**
 * iter554 · Inline claim-your-handle input on the logged-out landing.
 * Type a handle, tap Get Started → routes to /signup?handle=xxx so the
 * signup form pre-fills it. Live availability hint: while typing, we
 * hit /api/public/handle/available?slug=xxx (best-effort — silent on
 * failure). This is the strongest converting CTA on the page because
 * it turns "browse curiosity" into "already picked my name" in one
 * step. Modeled on hoo.be / linktr.ee's hero input.
 */
const FlytIdClaimInput = () => {
  const { setLoginOpen, setLoginMode } = useUserAuth();
  const [handle, setHandle] = useState('');
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const inputRef = useRef(null);
  const clean = (handle || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 30);
  const ready = clean.length >= 2 && status.state === 'available';

  // iter558 · Debounced availability check. Fires ~350ms after the
  // user stops typing so the input stays responsive without flooding
  // the backend. `checking` state shows a subtle spinner; `available`
  // shows green; `taken`/`invalid` show red. Errors silently fall
  // through — availability is a best-effort UX hint, not a gate
  // (backend re-validates at signup).
  useEffect(() => {
    if (clean.length < 2) { setStatus({ state: 'idle', message: '' }); return; }
    setStatus({ state: 'checking', message: 'Checking…' });
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/public/handle/available`, { params: { slug: clean } });
        if (res.data?.available) {
          setStatus({ state: 'available', message: 'This FLYT ID™ is available!' });
        } else {
          const reasonMap = {
            taken: 'Not available — try another',
            reserved: 'Reserved word — try another',
            invalid_chars: 'Only letters, numbers, and dashes',
            too_short: 'A bit longer please',
          };
          setStatus({ state: 'taken', message: reasonMap[res.data?.reason] || 'Not available' });
        }
      } catch {
        setStatus({ state: 'idle', message: '' });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [clean]);

  const submit = (e) => {
    e?.preventDefault?.();
    if (clean.length < 2) { inputRef.current?.focus(); return; }
    // iter561 · Open the in-app Sign-in / Create-account modal
    // (LoginModal) directly instead of navigating away — the user
    // already picked a FLYT ID™, we don't want them losing that
    // context to a full-page nav. Stash the handle on
    // sessionStorage so the athlete-onboarding step downstream can
    // pre-fill it. Default to `signup` mode since they just typed
    // a NEW handle (returning users would already be signed in);
    // the modal still exposes a "Already have an account? Sign in"
    // toggle for edge cases.
    try {
      window.sessionStorage.setItem('flyt:prefill_handle', clean);
    } catch (_) { /* private mode */ }
    setLoginMode?.('signup');
    setLoginOpen(true);
  };

  const ringColor =
    status.state === 'available' ? 'focus-within:ring-emerald-300/60 ring-emerald-400/25'
    : status.state === 'taken' ? 'focus-within:ring-rose-300/60 ring-rose-400/25'
    : 'focus-within:ring-white/25 ring-white/10';

  // iter560/562 · Liquid-glass pill — skinnier, softer, all-white text.
  // iter562 mobile pass: `max-w-xs` (was `max-w-md`) so the pill hugs
  // the copy above it and doesn't visually dominate the hero; button
  // label shortened + tighter padding so the whole control clocks
  // in ~40px tall on mobile. Autofill + Safari focus-outline overrides
  // live in /app/frontend/src/index.css.
  return (
    <div className="mt-5 sm:mt-6 max-w-xs sm:max-w-md">
      <form
        onSubmit={submit}
        className={`group flex items-center rounded-full bg-white/[0.04] ring-1 ${ringColor} focus-within:bg-white/[0.07] backdrop-blur-xl transition-all duration-200 pl-3.5 sm:pl-5 pr-1 py-0.5 sm:py-1`}
        style={{
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 24px -12px rgba(0,0,0,0.6)',
        }}
        data-testid="landing-claim-form"
      >
        <span className="pr-0.5 text-white/45 font-mono text-[11px] sm:text-[14px] tracking-tight select-none whitespace-nowrap">
          flytid.co/
        </span>
        <input
          ref={inputRef}
          type="text"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          autoCapitalize="off"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          placeholder="your-name"
          aria-label="Claim your FLYT ID™ handle"
          className="flex-1 min-w-0 bg-transparent outline-none text-white placeholder-white/30 font-mono text-[12px] sm:text-[14px] py-1.5 sm:py-2 caret-blue-400"
          style={{
            WebkitTextFillColor: '#ffffff',
            WebkitTapHighlightColor: 'transparent',
            transition: 'background-color 5000s ease-in-out 0s',
          }}
          data-testid="landing-claim-input"
        />
        <button
          type="submit"
          disabled={!ready}
          className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-white text-black text-[10px] sm:text-[13px] font-bold uppercase tracking-wider px-2.5 sm:px-4 py-1.5 hover:bg-blue-300 transition disabled:opacity-35 disabled:cursor-not-allowed disabled:hover:bg-white"
          data-testid="landing-claim-submit"
        >
          <span className="hidden sm:inline">Get started</span>
          <span className="sm:hidden">Start</span>
          <span aria-hidden="true" className="text-[12px] sm:text-[13px]">→</span>
        </button>
      </form>
      {/* iter616 · Download-on-the-App-Store badge sits directly under
          the FLYT ID handle input so the mobile Apple pill is the very
          first thing users see next to the Get Started CTA. Hides
          automatically inside the native iOS shell. */}
      <div className="mt-3 flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest text-white/40 font-bold">or</span>
        <AppStoreBadge size="sm" testid="landing-hero-app-store" />
      </div>
      {status.state !== 'idle' && (
        <p
          className={`mt-1.5 sm:mt-2 ml-4 sm:ml-5 text-[11px] sm:text-xs font-semibold ${
            status.state === 'available' ? 'text-emerald-300'
            : status.state === 'taken' ? 'text-rose-300'
            : 'text-white/50'
          }`}
          data-testid="landing-claim-status"
        >
          {status.state === 'available' && '✓ '}
          {status.state === 'taken' && '✕ '}
          {status.message}
        </p>
      )}
    </div>
  );
};


const PublicHomeLandingBlock = () => {
  const { setLoginOpen } = useUserAuth();
  const openLogin = () => setLoginOpen(true);
  const [athlete, setAthlete] = useState(null);
  const [stories, setStories] = useState([]);
  useEffect(() => {
    // Fetch athlete + stories in parallel so the profile card + articles
    // tab hydrate together. Both fall back gracefully if the API is
    // slow — the mockup shape still renders identically.
    // iter557 · Hard-prefer Jaxon by direct slug lookup so preview
    // never falls back to Ozzie when the athletes list is ordered
    // differently. On 404, degrade gracefully to the list scan.
    axios
      .get(`${API}/athletes/jaxon`)
      .then((r) => setAthlete(r.data || null))
      .catch(() => {
        axios
          .get(`${API}/athletes`, { params: { limit: 30 } })
          .then((r) => {
            const list = Array.isArray(r.data) ? r.data : [];
            const good =
              list.find((a) => (a.slug || '').startsWith('jaxon')) ||
              list.find((a) => a.photo) ||
              list[0];
            setAthlete(good || null);
          })
          .catch(() => setAthlete(null));
      });
    axios
      .get(`${API}/stories`, { params: { limit: 6 } })
      .then((r) => {
        setStories(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => setStories([]));
  }, []);

  return (
    <section
      className="relative bg-black text-white overflow-hidden"
      data-testid="public-home-landing-block"
    >
      {/* Pure black surface — matches the network hero above. No orb
          glows or gradient overlays so the two sections read as one
          continuous dark liquid-glass canvas. */}

      <div className="relative max-w-3xl lg:max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-14 space-y-6 sm:space-y-8">
        {/* Hero */}
        <div>
          <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] font-bold text-blue-300">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            The Athlete Career OS
          </p>
          <h1 className="mt-3 font-display uppercase tracking-tight leading-[0.95] text-3xl sm:text-5xl lg:text-6xl">
            Your career deserves
            <br />
            <span className="text-white/60">more than a </span>
            <span className="text-blue-300">highlight.</span>
          </h1>
          <p className="mt-4 text-sm sm:text-base text-white/70 leading-relaxed max-w-2xl">
            Your athlete career profile, your career timeline, your bio-link,
            and the tools your agent, brand, and programs use right alongside you.
          </p>
          {/* iter554 · Inline claim-your-handle input. Type a handle,
              tap Get Started → routes to signup with the handle
              pre-filled. Modeled after hoo.be/linktree's hero input.
              This is the single strongest CTA on the whole logged-out
              page because it converts intent to signup in one step. */}
          <FlytIdClaimInput />
        </div>

        {/* Section 01 · Profile card */}
        <SectionCard
          number="01"
          eyebrow="The Network Profile"
          title="Every athlete gets a home."
          body="A verified profile at flythouse.com/yourname — with your position, team, MaxPreps stats, partners, and business-inquiry button. This is exactly what recruiters and brands see."
          cta="Make mine now"
          ctaOnClick={openLogin}
          ctaTestid="public-landing-make-mine"
        >
          {/* iter555 · Liquid glass "FLYT NETWORK" pill above the card
              so viewers know this is what a real network profile
              looks like. Positioned inside the image well so it sits
              on the dark backdrop with a proper backdrop-blur. */}
          <div className="flex justify-center items-center gap-2 mb-4">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[10px] font-black uppercase tracking-[0.28em] text-white/85 ring-1 ring-white/15"
              style={{
                background: 'linear-gradient(180deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 100%)',
                backdropFilter: 'blur(14px)',
                WebkitBackdropFilter: 'blur(14px)',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
              FLYT Network
            </span>
            {/* iter557 · "See Jaxon" companion pill — matches the glass
                treatment but lives on a dark base so it reads as a
                secondary action. Opens his real profile at
                /network/jaxon in a new tab so the landing scroll
                position isn't lost. */}
            <Link
              to="/network/jaxon"
              className="inline-flex items-center gap-1 rounded-full bg-blue-500 hover:bg-blue-400 text-black text-[10px] font-black uppercase tracking-[0.28em] px-3.5 py-1.5 transition"
              data-testid="landing-see-jaxon"
            >
              See Jaxon
              <ArrowUpRight className="w-3 h-3" strokeWidth={3} />
            </Link>
          </div>
          <NetworkProfileVisual athlete={athlete} />
        </SectionCard>

        {/* Section 02 · Career Timeline */}
        <SectionCard
          number="02"
          eyebrow="The Journey"
          title="Every milestone, on one timeline."
          body="Free-agency signings, debuts, awards, personal bests — your whole story stacks vertically as it happens. Fans, recruiters, and future opportunities all read the same real record."
          cta="See a live timeline"
          ctaTo={`/network/${athlete?.slug || 'ozzie-albies'}`}
          ctaTestid="public-landing-section02-cta"
        >
          <CareerTimelineVisual />
        </SectionCard>

        {/* Section 03 · Bio-link */}
        <SectionCard
          number="03"
          eyebrow="Link in bio, upgraded"
          title="One link. Every highlight, deal, and coach."
          body="Drop your Flythouse URL in every Instagram bio and DM signature. Recruiters, brands, and fans land on the same beautifully-built page — with your video, links, and story all in one place."
          cta="Claim your link"
          ctaOnClick={openLogin}
          ctaTestid="public-landing-section03-cta"
        >
          <BioLinkBuilderVisual athlete={athlete} />
        </SectionCard>

        {/* Section 04 · Persona Explorer (tabbed) */}
        <PersonaExplorer />

        {/* Trust anchor */}
        <div className="rounded-2xl ring-1 ring-white/10 bg-white/[0.02] backdrop-blur-md p-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {['Verified athletes', 'Live NIL deals', 'Career-long profile'].map((t) => (
            <div key={t} className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-300 shrink-0" />
              <p className="text-[13px] text-white/80">{t}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PublicHomeLandingBlock;
